import { t } from "@lingui/core/macro";
import { confirmSignIn } from "aws-amplify/auth";
import { type FormEvent, useState } from "react";

import { type ChallengedUser } from "@/routes/_unauthenticated/sign-in";
import PasswordValidationTooltip from "@/shared/components/PasswordValidationTooltip";
import { AsyncButton } from "@/shared/components/ui/async-button";
import { Button } from "@/shared/components/ui/button";

import AuthPage from "./AuthPage";
import PasswordInput from "./PasswordInput";

export default function NewPassword({
  setChallengedUser,
  challengedUser,
}: {
  setChallengedUser: (challengedUser: ChallengedUser | null) => void;
  challengedUser: ChallengedUser;
}) {
  const [newPassword, setNewPassword] = useState("");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState("");

  const handleNewPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const password = formData.get("new-password") as string;
    const confirmPassword = formData.get("confirm-password") as string;

    if (password !== confirmPassword) {
      setError(t`Passwords do not match. Please try again.`);

      return;
    }

    setError("");
    setLoadingSubmit(true);
    try {
      const result = await confirmSignIn({ challengeResponse: password });
      if (result.isSignedIn) {
        setChallengedUser(null);
      } else if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        setChallengedUser({ challengeName: "SOFTWARE_TOKEN_MFA", email: challengedUser.email });
      } else {
        setError(
          t`We were unable to sign you in to your account. Please try again later or reach out to support if the issue persists.`,
        );
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <AuthPage title={t`Reset Password`}>
      <form className="flex flex-col gap-4" onSubmit={handleNewPassword}>
        <PasswordValidationTooltip password={newPassword}>
          <PasswordInput
            placeholder={t`Enter your New Password`}
            autoComplete="new-password"
            name="new-password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </PasswordValidationTooltip>
        <PasswordInput
          placeholder={t`Confirm your New Password`}
          autoComplete="new-password"
          name="confirm-password"
          required
        />
        {error && <p className="text-sm text-red-500">{error}</p>}
        <AsyncButton type="submit" loading={loadingSubmit} loadingText={t`Submitting`}>
          {t`Submit`}
        </AsyncButton>
        <Button
          variant="link"
          onClick={() => {
            setChallengedUser(null);
          }}
        >
          {t`Back to Sign In`}
        </Button>
      </form>
    </AuthPage>
  );
}
